import { getState, setState, subscribe, LOCK_MS } from './store.js';
import { setMuted, isMuted } from './audio.js';
import { closeGame } from './bridge.js';

let onAmountChanged = null;
let mountedRoot = null;
let activityPanelVisible = false;
let lastActivityHeadId = null;

/**
 * "Greedy BIGO" HUD layout:
 *   ┌──────────────────────────────────────────┐
 *   │  ←   Greedy BIGO              🔊  ✕      │  topbar
 *   ├──────────────────────────────────────────┤
 *   │                                          │
 *   │           [ Phaser canvas ]              │
 *   │                                          │
 *   ├──────────────────────────────────────────┤
 *   │  Choose the amount of wager > food       │
 *   │   ⊙10   ⊙50   ⊙100   ⊙1000              │
 *   │   Coins left 💎 12345  Today's profits ◇ │
 *   │   Result   🍕 🥕 🌭 🥬 🌭 🍅 🥩 🌽       │
 *   └──────────────────────────────────────────┘
 */
export function mountHud(rootId = 'hud-root', { onAmountSelected, onClose, onMuteToggle } = {}) {
  mountedRoot = document.getElementById(rootId);
  mountedRoot.innerHTML = '';
  onAmountChanged = onAmountSelected;
  activityPanelVisible = false;
  lastActivityHeadId = null;

  // ---- Top bar ----
  const topbar = el('div', 'topbar');
  const left = el('div', 'topbar-left');
  const back = el('button', 'icon-btn');
  back.type = 'button';
  back.setAttribute('aria-label', 'Back');
  back.title = 'Back';
  back.innerHTML = '←';
  back.onclick = () => { if (onClose) onClose(); else closeGame(); };
  left.appendChild(back);

  const title = el('div', 'title');
  // Left empty on purpose: the flex:1 title cell keeps the back button and
  // the right-hand icons pinned to opposite edges.
  title.setAttribute('aria-hidden', 'true');

  const right = el('div', 'topbar-right');
  const muteBtn = el('button', 'icon-btn');
  muteBtn.type = 'button';
  muteBtn.setAttribute('aria-label', isMuted() ? 'Unmute' : 'Mute');
  muteBtn.setAttribute('aria-pressed', String(isMuted()));
  muteBtn.title = isMuted() ? 'Unmute' : 'Mute';
  muteBtn.innerHTML = isMuted() ? '🔇' : '🔊';
  muteBtn.onclick = () => {
    setMuted(!isMuted());
    muteBtn.innerHTML = isMuted() ? '🔇' : '🔊';
    muteBtn.setAttribute('aria-label', isMuted() ? 'Unmute' : 'Mute');
    muteBtn.setAttribute('aria-pressed', String(isMuted()));
    muteBtn.title = isMuted() ? 'Unmute' : 'Mute';
    if (onMuteToggle) onMuteToggle(isMuted());
  };
  right.appendChild(muteBtn);

  const historyButton = el('button', 'icon-btn history-button');
  historyButton.id = 'activity-history-button';
  historyButton.type = 'button';
  historyButton.setAttribute('aria-label', 'Activity');
  historyButton.setAttribute('aria-controls', 'activity-panel');
  historyButton.setAttribute('aria-expanded', 'false');
  historyButton.setAttribute('aria-pressed', 'false');
  historyButton.title = 'Open activity';
  historyButton.innerHTML = '↺';
  historyButton.onclick = () => {
    activityPanelVisible = !activityPanelVisible;
    render(getState());
  };
  right.appendChild(historyButton);

  const helpBtn = el('button', 'icon-btn');
  helpBtn.type = 'button';
  helpBtn.setAttribute('aria-label', 'How to play');
  helpBtn.title = 'How to play';
  helpBtn.innerHTML = '?';
  helpBtn.style.fontWeight = '900';
  helpBtn.onclick = () => showRulesSheet(getState());
  right.appendChild(helpBtn);

  topbar.appendChild(left);
  topbar.appendChild(title);
  topbar.appendChild(right);
  mountedRoot.appendChild(topbar);

  const activityPanel = el('section', 'activity-panel');
  activityPanel.id = 'activity-panel';
  activityPanel.hidden = true;
  activityPanel.setAttribute('aria-label', 'Game activity');
  activityPanel.innerHTML = `
    <div class="activity-head">
      <div class="activity-headline">
        <span>Activity</span>
        <span class="activity-live">Totals</span>
        <span class="activity-count" id="activity-count"></span>
      </div>
      <button class="activity-toggle" id="activity-toggle" type="button" aria-controls="activity-list" aria-expanded="true" title="Close history"><span class="activity-toggle-icon">×</span><span class="activity-toggle-label">Close</span></button>
    </div>
    <div class="activity-list" id="activity-list"></div>
  `;
  const activityToggle = activityPanel.querySelector('#activity-toggle');
  if (activityToggle) {
    activityToggle.onclick = () => {
      activityPanelVisible = false;
      render(getState());
    };
  }
  mountedRoot.appendChild(activityPanel);

  // Spacer fills the middle (Phaser canvas is behind via z-index)
  const spacer = el('div', 'spacer');
  mountedRoot.appendChild(spacer);

  // ---- Bottom panel ----
  const panel = el('div', 'bottom-panel');

  const caption = el('div', 'panel-caption');
  caption.id = 'hud-caption';
  caption.setAttribute('role', 'status');
  caption.setAttribute('aria-live', 'polite');
  caption.textContent = 'Pick an amount, then tap a slot';
  panel.appendChild(caption);

  const coinsRow = el('div', 'coins-row');
  coinsRow.id = 'coins-row';
  panel.appendChild(coinsRow);

  const limits = el('div', 'panel-limits');
  limits.id = 'hud-limits';
  panel.appendChild(limits);

  const statsRow = el('div', 'stats-row');
  const coinsPill = el('div', 'stat-pill');
  coinsPill.innerHTML = `<span class="label">Diamonds</span><span class="value balance-value">💎 <span id="balance-amount">—</span></span>`;
  const wagerPill = el('div', 'stat-pill');
  wagerPill.innerHTML = `<span class="label">Round bet</span><span class="value wager">🎯 <span id="wager-amount">0</span></span>`;
  const profitPill = el('div', 'stat-pill');
  profitPill.innerHTML = `<span class="label">Session profit</span><span class="value profit">📈 <span id="profit-amount">0</span></span>`;
  statsRow.appendChild(coinsPill);
  statsRow.appendChild(wagerPill);
  statsRow.appendChild(profitPill);
  panel.appendChild(statsRow);

  const resultStrip = el('div', 'result-strip');
  const resultLabel = el('span', 'label');
  resultLabel.textContent = 'Result';
  resultStrip.appendChild(resultLabel);
  const resultsList = el('div', 'results-list');
  resultsList.id = 'results-list';
  resultStrip.appendChild(resultsList);
  panel.appendChild(resultStrip);

  mountedRoot.appendChild(panel);

  subscribe(render);
  render(getState());
}

function render(state) {
  if (!mountedRoot) return;

  // Caption changes by game phase. Use the same bettable-window math as the
  // Phaser scene (floor of remaining minus LOCK_MS) so all surfaces agree.
  const cap = document.getElementById('hud-caption');
  if (cap) {
    if (!state.config || !state.config.enabled) {
      cap.textContent = 'Game disabled';
    } else if (state.status === 'betting') {
      // Display real seconds remaining. ceil so we don't linger on a digit
      // for the ~1 s window the server is already counting down.
      const ms = Math.max(0, state.remainingMs || 0);
      const sec = Math.ceil(ms / 1000);
      cap.textContent = ms > LOCK_MS
        ? `Place your bets — ${sec}s`
        : 'Locking…';
    } else if (state.status === 'spinning') {
      cap.textContent = 'Spinning the wheel…';
    } else if (state.status === 'result') {
      cap.textContent = 'Round complete';
    } else {
      cap.textContent = 'Pick an amount, then tap a slot';
    }
  }

  // Balance, total round wager, and session profit
  const balEl = document.getElementById('balance-amount');
  if (balEl) balEl.textContent = state.balance == null ? '—' : formatNum(state.balance);
  const wagerEl = document.getElementById('wager-amount');
  if (wagerEl) {
    const total = Object.values(state.myBets || {}).reduce((a, b) => a + (b || 0), 0);
    wagerEl.textContent = formatNum(total);
  }
  const profitEl = document.getElementById('profit-amount');
  if (profitEl) {
    const v = state.sessionProfit || 0;
    profitEl.textContent = (v < 0 ? '-' : '') + formatNum(Math.abs(v));
    profitEl.classList.toggle('negative', v < 0);
  }

  const activityPanel = document.getElementById('activity-panel');
  const activityToggle = document.getElementById('activity-toggle');
  const historyButton = document.getElementById('activity-history-button');
  const activityCount = document.getElementById('activity-count');
  const activityRows = state.activity || [];
  const isActivityOpen = activityPanelVisible;
  if (activityPanel) {
    activityPanel.hidden = !isActivityOpen;
    activityPanel.classList.toggle('expanded', isActivityOpen);
  }
  if (historyButton) {
    historyButton.hidden = false;
    historyButton.title = isActivityOpen ? 'Close activity' : 'Open activity';
    historyButton.setAttribute('aria-label', isActivityOpen ? 'Close activity' : 'Activity');
    historyButton.setAttribute('aria-expanded', String(isActivityOpen));
    historyButton.setAttribute('aria-pressed', String(isActivityOpen));
  }
  if (activityToggle) {
    activityToggle.hidden = false;
    activityToggle.disabled = false;
    activityToggle.title = 'Close history';
    activityToggle.setAttribute('aria-label', 'Close history');
    activityToggle.setAttribute('aria-expanded', String(isActivityOpen));
    const icon = activityToggle.querySelector('.activity-toggle-icon');
    const label = activityToggle.querySelector('.activity-toggle-label');
    if (icon) icon.textContent = '×';
    if (label) label.textContent = 'Close';
  }
  if (activityCount) {
    activityCount.textContent = activityRows.length === 0
      ? ''
      : `${activityRows.length} users`;
  }

  // Coin chips
  const cr = document.getElementById('coins-row');
  if (cr) {
    const cfg = state.config;
    const list = ((cfg && cfg.quickBetAmounts) || []).slice(0, 4);
    while (list.length < 4) list.push(null);
    const expectedHash = list.join(',');
    if (cr.dataset.hash !== expectedHash) {
      cr.innerHTML = '';
      list.forEach((amt, idx) => {
        const chip = el('button', 'coin-chip');
        chip.type = 'button';
        if (amt == null) {
          chip.style.visibility = 'hidden';
          chip.setAttribute('aria-hidden', 'true');
          chip.tabIndex = -1;
          cr.appendChild(chip);
          return;
        }
        // Tier 1–4 by ascending amount rank in the quick-bet list.
        const tier = Math.min(4, idx + 1);
        chip.dataset.tier = String(tier);
        chip.dataset.amount = String(amt);
        chip.setAttribute('aria-label', `Bet ${amt} diamonds`);
        chip.setAttribute('aria-pressed', 'false');
        const face = el('div', 'coin-face');
        face.textContent = formatNum(amt);
        chip.appendChild(face);
        chip.onclick = () => {
          setState({ selectedAmount: amt });
          if (onAmountChanged) onAmountChanged(amt);
        };
        cr.appendChild(chip);
      });
      cr.dataset.hash = expectedHash;
    }
    Array.from(cr.children).forEach((c) => {
      const sel = Number(c.dataset.amount) === state.selectedAmount;
      c.classList.toggle('selected', sel);
      if (c.dataset.amount) c.setAttribute('aria-pressed', String(sel));
    });
  }

  // Result history strip
  const rl = document.getElementById('results-list');
  if (rl) {
    const hist = state.resultHistory || [];
    const expectedHash = hist.map((r) => `${r.slotId}:${r.isNew ? 'n' : 'o'}:${r.band || ''}`).join(',');
    if (rl.dataset.hash !== expectedHash) {
      rl.innerHTML = '';
      hist.slice(0, 8).forEach((r) => {
        const pip = el('div', 'result-pip');
        if (r.band) pip.dataset.band = r.band;
        if (r.multiplier) pip.title = `×${r.multiplier}`;
        pip.textContent = r.emoji || '🎁';
        if (r.isNew) {
          const nb = el('span', 'new-badge');
          nb.textContent = 'New';
          pip.appendChild(nb);
        }
        rl.appendChild(pip);
      });
      rl.dataset.hash = expectedHash;
    }
  }

  // Limits caption (proactive guard rails)
  const lim = document.getElementById('hud-limits');
  if (lim) {
    const cfg = state.config;
    if (!cfg) {
      lim.textContent = '';
    } else {
      const parts = [];
      if (cfg.maxSpotsPerUser) parts.push(`Max ${cfg.maxSpotsPerUser} slots`);
      if (cfg.minBetAmount) parts.push(`Min 💎 ${formatNum(cfg.minBetAmount)}`);
      if (cfg.slotMaxAmount) parts.push(`Slot cap 💎 ${formatNum(cfg.slotMaxAmount)}`);
      lim.textContent = parts.join(' · ');
    }
  }

  renderActivity(state);
}

function renderActivity(state) {
  const listEl = document.getElementById('activity-list');
  if (!listEl) return;
  if (!activityPanelVisible) {
    listEl.innerHTML = '';
    listEl.dataset.hash = 'hidden';
    return;
  }
  const allRows = state.activity || [];
  const rows = allRows.slice(0, 20);
  const latestId = allRows[0] && allRows[0]._id ? String(allRows[0]._id) : null;
  const hasFreshHead = !!latestId && latestId !== lastActivityHeadId;
  if (latestId) lastActivityHeadId = latestId;
  const expectedHash = `open::${rows
    .map((r) => `${r._id || ''}:${r.userName || ''}:${r.customUserId || r.publicUserId || ''}:${r.totalBetAmount || 0}:${r.totalWinnings || 0}:${r.totalLosses || 0}:${r.netAmount || 0}:${r.roundCount || 0}:${r.lastActivityAt || ''}`)
    .join('|')}`;
  if (listEl.dataset.hash === expectedHash) return;
  listEl.dataset.hash = expectedHash;
  if (rows.length === 0) {
    listEl.innerHTML = '<div class="activity-empty">No activity yet</div>';
    return;
  }
  listEl.innerHTML = rows.map((row, index) => {
    const name = row.userName || 'Player';
    const customUserId = row.customUserId || row.publicUserId || '';
    const initial = name.trim().slice(0, 1).toUpperCase() || 'P';
    const win = Math.max(0, Math.floor(Number(row.totalWinnings || 0)));
    const loss = Math.max(0, Math.floor(Number(row.totalLosses || 0)));
    const net = Math.floor(Number(row.netAmount || (win - loss)));
    const trend = net >= 0 ? 'win' : 'loss';
    const rowClasses = ['activity-row'];
    if (index === 0 && hasFreshHead) rowClasses.push('is-fresh');
    const roundCount = Math.max(0, Math.floor(Number(row.roundCount || 0)));
    const roundLabel = roundCount === 1 ? '1 round' : `${roundCount} rounds`;
    const roundText = roundCount > 0 ? roundLabel : 'Totals';
    return `
      <div class="${rowClasses.join(' ')}" data-trend="${trend}">
        <div class="activity-avatar"${row.userAvatar ? ` style="background-image:url('${escapeAttr(row.userAvatar)}');background-size:cover;"` : ''}>${row.userAvatar ? '' : escapeHtml(initial)}</div>
        <div class="activity-main">
          <div class="activity-name">${escapeHtml(name)}</div>
          <div class="activity-meta">
            <span>${escapeHtml(roundText)}</span>
            ${customUserId ? `<span class="activity-user-id">ID ${escapeHtml(customUserId)}</span>` : ''}
          </div>
        </div>
        <div class="activity-amounts">
          <span class="activity-win">+💎 ${formatNum(win)}</span>
          <span class="activity-loss">-💎 ${formatNum(loss)}</span>
        </div>
      </div>`;
  }).join('');
  listEl.scrollTop = 0;
}

export function showToast(msg, kind = 'success', durationMs = 2200) {
  const t = el('div', `toast ${kind}`);
  t.setAttribute('role', kind === 'error' ? 'alert' : 'status');
  t.setAttribute('aria-live', kind === 'error' ? 'assertive' : 'polite');
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => { t.style.opacity = '0'; t.style.transition = 'opacity 0.25s'; }, durationMs - 250);
  setTimeout(() => { t.remove(); }, durationMs);
}

function el(tag, cls) {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}

function formatNum(n) {
  if (n == null || isNaN(n)) return '—';
  return String(Math.floor(Number(n))).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * Brief full-screen "BETTING LOCKED" sweep, shown when the round transitions
 * from betting → spinning. Auto-removes after the CSS animation completes.
 */
export function showLockedSweep() {
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const overlay = el('div', 'locked-overlay');
  overlay.setAttribute('aria-hidden', 'true');
  const pill = el('div', 'pill');
  pill.textContent = 'BETTING LOCKED';
  overlay.appendChild(pill);
  document.body.appendChild(overlay);
  setTimeout(() => overlay.remove(), 800);
}

/**
 * Gentle consolation animation for losing/non-betting players (no shake, no audio).
 */
export function showConsolation() {
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const node = el('div', 'consolation');
  node.setAttribute('aria-hidden', 'true');
  node.textContent = '🍀';
  document.body.appendChild(node);
  setTimeout(() => node.remove(), 1800);
}

/**
 * Rules / how-to-play sheet. Mirrors the result-sheet structure so close
 * interaction is consistent. Includes payout grid + provably-fair pre-commit
 * info for the *upcoming* round when available (state.round.serverSeedHash).
 */
export function showRulesSheet(state) {
  const root = document.getElementById('bottom-sheet-root');
  if (!root) return;
  // Preserve any existing result sheet — render rules above it.
  const existed = root.children.length;
  if (existed) root.innerHTML = '';

  const cfg = state.config || {};
  const slots = cfg.slots || [];

  const backdrop = el('div', 'sheet-backdrop');
  const sheet = el('div', 'bottom-sheet rules-sheet');
  const close = () => { root.innerHTML = ''; };
  backdrop.onclick = close;

  const slotsHtml = slots
    .map((s) => `<div class="mult-cell"><span class="e">${s.emoji || '🎁'}</span><span>${escapeHtml(s.name || '')}</span><div class="m">×${s.multiplier}</div>${s.probability ? `<div class="p">${formatPct(s.probability)} of spins</div>` : ''}</div>`)
    .join('');

  const round = state.round || {};
  const fairnessHtml = round.serverSeedHash
    ? `<section><h3>🔒 Fairness — next round</h3>
         <p>Server hash is committed before bets open. After the spin, the server reveals the seed so you can verify the result.</p>
         <div class="precommit">${escapeHtml(round.serverSeedHash)}</div>
         ${round.oddsHash ? `<p>Odds hash (the odds above, committed with the seed):</p><div class="precommit">${escapeHtml(round.oddsHash)}</div>` : ''}
       </section>`
    : '';

  const limitsHtml = `
    <ul>
      ${cfg.maxSpotsPerUser ? `<li>Up to <b>${cfg.maxSpotsPerUser}</b> different slots per round.</li>` : ''}
      ${cfg.minBetPerSlot ? `<li>Minimum bet: <b>💎 ${formatNum(cfg.minBetPerSlot)}</b>.</li>` : ''}
      ${cfg.maxBetPerSlot ? `<li>Maximum per slot: <b>💎 ${formatNum(cfg.maxBetPerSlot)}</b>.</li>` : ''}
      ${cfg.roundDurationSec ? `<li>Betting window: <b>${cfg.roundDurationSec}s</b>.</li>` : ''}
    </ul>`;

  sheet.innerHTML = `
    <div class="sheet-handle"></div>
    <h2>How to play</h2>
    <section>
      <h3>The basics</h3>
      <ol style="padding-left:18px;margin:4px 0;">
        <li>Pick a chip amount.</li>
        <li>Tap one or more slots on the wheel to bet that amount.</li>
        <li>Wait for the wheel to spin — the highlighted slot wins.</li>
        <li>If your slot wins, you receive <b>bet × multiplier</b>.</li>
      </ol>
    </section>
    <section>
      <h3>Slots, multipliers &amp; odds</h3>
      <div class="multiplier-grid">${slotsHtml || '<div class="mult-cell">—</div>'}</div>
      ${cfg.rtp ? `<p class="rtp-note">Every slot pays back <b>${formatPct(cfg.rtp)}</b> of what is bet on it, on average over many rounds (RTP). That is not the chance of winning: higher multipliers win less often. Odds are fixed and never depend on who bet what.</p>` : ''}
    </section>
    <section>
      <h3>Limits</h3>
      ${limitsHtml}
    </section>
    ${fairnessHtml}
    <button class="sheet-close-btn" type="button">Got it</button>
  `;
  const closeBtn = sheet.querySelector('.sheet-close-btn');
  if (closeBtn) closeBtn.onclick = close;

  root.appendChild(backdrop);
  root.appendChild(sheet);
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function escapeAttr(s) { return escapeHtml(s); }

function formatPct(x) {
  const v = Number(x) * 100;
  return `${Number.isInteger(Math.round(v * 10) / 10) ? Math.round(v) : v.toFixed(1)}%`;
}
